import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "src/async/apiUtils";
import { WorkspaceContext } from "src/context/workspace";
import { useWorkspaceSwitching } from "src/context/WorkspaceSwitchingContext";
import { getNavigateLink } from "src/helpers/userHelper";
import useUrlQueryParams from "./useUrlQueryParams";
import { SiteContext } from "src/context/site";
import { setBrowserStyleProperties } from "src/helpers/setBrowserStyleProperties";
import { toast } from "react-toastify";

export const useWorkspaceManager = () => {
  const navigate = useNavigate();
  const { site } = useContext(SiteContext);
  const { workspace_id } = useParams();
  const { startSwitching, finishSwitching } = useWorkspaceSwitching();

  const { group: group_id, page: page_id, document: document_id, form: form_id } = useUrlQueryParams();
  const { details: workspaceDetails, setDetails: setWorkspaceDetails, setLoading: setWorkspaceDetailsLoading, setPlanDetails, setIsPlanDetailsLoaded, setWorspacePaymentModalOpen } = useContext(WorkspaceContext);

  const [loading, setLoading] = useState(false);
  const [currentLogo, setCurrentLogo] = useState("");
  const [switchAdminButtonDisabled, setSwitchAdminButtonDisabled] = useState(false);
  const [settingsSlider, setSettingsSlider] = useState(false);
  const [isContent, setIsContent] = useState(true);

  const clickWorkspace = async (workspace = {}, from = "workspace") => {
    let workspaceId = workspace?._id;

    if (!workspaceId) {
      throw new Error("No workspace available to fetch.");
    }

    if ("" + workspaceId === "" + workspaceDetails?._id && from !== "payments") {
      return;
    }

    startSwitching(workspace, workspaceDetails);
    localStorage.setItem("workspace", workspace?.slug || workspace?._id || "");
    localStorage.setItem("group", "");
    localStorage.setItem("page", "");

    setIsPlanDetailsLoaded(false);
    setWorkspaceDetailsLoading(true);

    try {
      const { data: resData } = await apiRequest("GET", `/portal/users/${workspaceId}/page`, {}, { returnErrorWithstatus: true });

      if (resData.status === 200) {
        if (workspaceDetails._id !== workspace._id) {
          setWorkspaceDetails(workspace);
        }
        setIsContent(resData?.is_content);
        const navigate_link = getNavigateLink(resData);
        navigate(navigate_link);
      } else {
        navigate("/permission-denied");
      }
    } catch (err) {
      setIsContent(false);
      if (err?.response?.data?.status === 402) {
        navigate(`/workspace/${workspace?.slug}/payment-required`);
      } else {
        navigate("/permission-denied");
      }
    } finally {
      setWorkspaceDetailsLoading(false);
    }
  };

  const loadPlanDetails = async (workspaceId, refresh = false) => {
    try {
      if (!refresh) {
        setIsPlanDetailsLoaded(false);
        setPlanDetails(null);
      }
      setLoading(true);
      let url = `/portal/workspaces/${workspaceId}/billing/plan`;
      const pageQuery = page_id ? `&page=${page_id}` : document_id ? `&document=${document_id}` : form_id ? `&form=${form_id}` : "";
      url = `${url}?group=${group_id || ""}${pageQuery}`;

      const { data } = await apiRequest("get", url, {}, { returnErrorWithstatus: true });
      setPlanDetails(data?.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setPlanDetails(null);
    } finally {
      setIsPlanDetailsLoaded(true);
      if (!isContent) {
        finishSwitching();
      }
    }
  };

  const navigateToPaymemnts = () => {
    setWorspacePaymentModalOpen(true);
  };

  const navigateToSettings = () => {
    setSettingsSlider(true);
  };

  const extractWorkspaceName = () => {
    const url = window.location.href;
    const pathSegments = url.split("/");

    const workspaceIndex = pathSegments.findIndex((segment) => segment === "workspace");

    if (workspaceIndex !== -1 && workspaceIndex < pathSegments.length - 1) {
      const workspaceName = pathSegments[workspaceIndex + 1];
      return workspaceName;
    }

    return null;
  };

  const loadImage = () => {
    let src = site?.image_logo ? site.image_logo : "";
    let setting = {};
    if (workspace_id && workspaceDetails?.use_global_settings) {
      setting = site;
      src = site?.image_logo ? site.image_logo : "";
    } else {
      src = workspaceDetails?.image_logo ? workspaceDetails.image_logo : "";
      setting = workspaceDetails;
    }
    setCurrentLogo(src);
    setBrowserStyleProperties(document, setting);
  };

  const onHandleSwitchToAdmin = async () => {
    try {
      setSwitchAdminButtonDisabled(true);
      const { data } = await apiRequest("get", "/portal/users/self/switch-to-admin");
      window.open(data?.data, "_blank");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSwitchAdminButtonDisabled(false);
    }
  };

  return { loading, currentLogo, switchAdminButtonDisabled, clickWorkspace, loadPlanDetails, navigateToPaymemnts, navigateToSettings, extractWorkspaceName, loadImage, onHandleSwitchToAdmin, settingsSlider, setSettingsSlider };
};
